/**
 * Live procurement deals aligned with each tenant's primary categories
 * (same source as Procurement Live Radar + product profile category_primary).
 */

import { getDealCategoriesForTenant } from '@/lib/procurementCompetitors';
import { categoryMatches, getDealCategoryDisplay, normalizeCategoryValue } from '@/utils/procurementCategoryUtils';
import {
  toOfficialCategoryName,
  getProfileProcurementCategories,
  encodeCategoriesQueryParam,
} from '@/utils/edtechCategoryNames';
import { fetchProductProfiles } from '@/lib/api/fit';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

/** Tenant defaults (TENANT_CATEGORIES) + active product profile primary categories. */
export function mergePrimaryProcurementCategories(tenantId, profilePrimaries = []) {
  const fromTenant = getDealCategoriesForTenant(tenantId) || [];
  const fromProfiles = (profilePrimaries || [])
    .map((c) => toOfficialCategoryName(c) || normalizeCategoryValue(String(c)))
    .filter(Boolean);
  return [...new Set([...fromTenant, ...fromProfiles])].sort((a, b) =>
    (a || '').localeCompare(b || '', undefined, { sensitivity: 'base' })
  );
}

export async function fetchPrimaryProcurementCategoriesForTenant(tenantId) {
  let profilePrimaries = [];
  if (tenantId) {
    try {
      const profiles = await fetchProductProfiles(tenantId);
      profilePrimaries = (profiles || [])
        .filter((p) => p.is_active !== false)
        .flatMap((p) => getProfileProcurementCategories(p));
    } catch (e) {
      console.warn('[procurementTenantLiveDeals] product profiles:', e);
    }
  }
  return mergePrimaryProcurementCategories(tenantId, profilePrimaries);
}

/** Match deal to primary categories (deal.category + matched_categories). */
export function dealMatchesPrimaryProcurementCategories(deal, primaryCategories) {
  if (!primaryCategories?.length) return true;
  if (categoryMatches(primaryCategories, deal)) return true;
  const fromMatched = (deal?.matched_categories || [])
    .map((c) => (typeof c === 'string' ? c : c?.name || c?.category || ''))
    .filter(Boolean)
    .join('|');
  if (fromMatched && categoryMatches(primaryCategories, fromMatched)) return true;
  return false;
}

export function filterLiveDealsByPrimaryCategories(deals, primaryCategories) {
  return (deals || []).filter((deal) => {
    const status = String(deal?.status || '').trim().toLowerCase();
    if (status && status !== 'live') return false;
    return dealMatchesPrimaryProcurementCategories(deal, primaryCategories);
  });
}

/** Same API as Market / Procurement Live Radar (`table=live`). */
export async function fetchLiveDealsFromProcurementApi(
  tenantId,
  { categories = null, limit = 500, maxPages = 10 } = {}
) {
  if (!tenantId) return [];
  const categoriesParam = (categories || []).length
    ? `&categories=${encodeCategoriesQueryParam(categories)}`
    : '';

  const allItems = [];
  let currentCursor = null;
  let page = 0;

  do {
    page += 1;
    if (page > maxPages) break;
    const url = currentCursor
      ? `${API_BASE_URL}/api/procurement/query?table=live&limit=${limit}&skipFit=true${categoriesParam}&cursor=${encodeURIComponent(JSON.stringify(currentCursor))}`
      : `${API_BASE_URL}/api/procurement/query?table=live&limit=${limit}&skipFit=true${categoriesParam}`;
    const res = await fetch(url, { headers: { 'x-tenant-id': tenantId }, credentials: 'include' });
    const pageData = await res.json().catch(() => ({}));
    if (!res.ok) {
      console.warn('[procurementTenantLiveDeals] live query failed:', pageData?.error || res.status);
      break;
    }
    const items = pageData.data || [];
    allItems.push(...items);
    currentCursor = pageData.nextCursor;
  } while (currentCursor);

  return allItems;
}

export function getProcurementDeadline(deal) {
  return (
    deal?.deadline_date ||
    deal?.deadline ||
    deal?.due_date ||
    deal?.closing_date ||
    deal?.submission_deadline ||
    null
  );
}

/** Active live deals: primary categories + future or open deadline (Live Radar behavior). */
export function filterActiveLiveDeals(deals, primaryCategories) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const categoryFiltered = primaryCategories?.length
    ? filterLiveDealsByPrimaryCategories(deals, primaryCategories)
    : (deals || []).filter((d) => String(d?.status || '').toLowerCase() === 'live' || !d?.status);

  return categoryFiltered
    .filter((p) => {
      const dl = getProcurementDeadline(p);
      if (!dl || dl === 'N/A' || (typeof dl === 'string' && dl.trim() === '')) return true;
      try {
        const deadlineDate = new Date(dl);
        deadlineDate.setHours(0, 0, 0, 0);
        return deadlineDate >= today;
      } catch {
        return true;
      }
    })
    .sort((a, b) => {
      const aDl = getProcurementDeadline(a);
      const bDl = getProcurementDeadline(b);
      const aHas =
        aDl && aDl !== 'N/A' && (typeof aDl !== 'string' || aDl.trim() !== '');
      const bHas =
        bDl && bDl !== 'N/A' && (typeof bDl !== 'string' || bDl.trim() !== '');
      if (aHas && !bHas) return -1;
      if (!aHas && bHas) return 1;
      if (!aHas && !bHas) return 0;
      try {
        return new Date(aDl) - new Date(bDl);
      } catch {
        return 0;
      }
    });
}

export { getDealCategoryDisplay };
